import { Token, T } from './lexer.types';
import { sanitizeInput } from './text';

const TOKEN_BOOK = /[123]*[a-z]+/;
const TOKEN_OPERATORS = /[\,\-\:\;]/;
const TOKEN_NUMBER = /\d+/;

/**
 * NOTE:
 * I didn't want to make a lookahead lexer, so I use a slightly less efficient
 * approach that repeatedly scans for books until they are exhausted, before
 * moving on to the easier tokens to identify. Books are a bit hard because they
 * can lead off with a number, but the number can be part of a book name.
 * Typically that requires a look-ahead, but if we look for numbers in front of
 * text, we can identify those as books first, and get them out of the way.
 */

export function lexer(input: string): T[] {
    input = sanitizeInput(input);

    // Can we look for book first, split the rest out, and then look for
    // operators, and then numbers?
    const pass1 = parseBooks(input);
    const pass2 = pass1.flatMap((x) =>
        typeof x === 'string' ? parseOperators(x) : [x]
    );
    const pass3 = pass2.flatMap((x) =>
        typeof x === 'string' ? parseNumbers(x) : [x]
    );
    return predicateBooksWithSemicolon(<Token[]>pass3.filter((x) => x !== ''));
}

export function parseBooks(input: string): T[] {
    return parseGeneric(input, 'book', TOKEN_BOOK);
}
export function parseOperators(input: string): T[] {
    return parseGeneric(input, 'op', TOKEN_OPERATORS);
}
export function parseNumbers(input: string): T[] {
    const tokens = parseGeneric(input, 'number', TOKEN_NUMBER);
    return tokens.map((t: any) =>
        t.type === 'number' ? { type: 'number', value: parseInt(t.value) } : t
    );
}

export function parseGeneric(
    input: string,
    tokenType: string,
    regexPattern: any
): T[] {
    // TODO: we need to store the token type, and token string in a defined
    // type, and return an array of all these token types. We are going ot
    // recursively call this function on books until we don't have a match, then
    // go on to operators, repeat, and then go on to the numbers. After we
    // combine them, we can start to implement the grammar of tokens into an
    // AST.
    let tokens: T[] = [];
    const parts = input.match(regexPattern);
    if (parts) {
        // @ts-ignore
        const rightStart = parts.index + parts[0].length;
        // @ts-ignore
        if (parts.index > 0) {
            const left = input.slice(0, parts.index);
            const leftTokens =
                left.length > 0
                    ? parseGeneric(left, tokenType, regexPattern)
                    : [];
            const middle: Token = { type: tokenType, value: parts[0] };
            const right = input.slice(rightStart, input.length);
            const rightTokens =
                right.length > 0
                    ? parseGeneric(right, tokenType, regexPattern)
                    : [];
            tokens = [...leftTokens, middle, ...rightTokens];
        } else {
            const middle: Token = { type: tokenType, value: parts[0] };
            const right: string = input.slice(rightStart, input.length);
            const rightTokens =
                right.length > 0
                    ? parseGeneric(right, tokenType, regexPattern)
                    : [];
            tokens = [middle, ...rightTokens];
        }
    }
    return tokens.length > 0 ? tokens : [input];
}

/**
 * Looks for books that may have not have a semicolon separating them.
 * This an lead to bad book names, like "john 1,2 john 1:1", being
 * interpreted as "john 1,; 2 john 1:1".
 */
export function predicateBooksWithSemicolon(tokens: Token[]) {
    for (let i = 1; i < tokens.length; i++) {
        if (tokens[i].type === 'book' && tokens[i - 1].value !== ';') {
            // We need to insert a semicolon, but need to figure out
            // if we need to adjust the current book name.
            if (
                String(tokens[i].value).match(/^\d/) &&
                tokens[i - 1].type === 'number'
            ) {
                // The book name starts with a number, so we need to
                // insert a semicolon before the number.
                tokens = [
                    ...tokens.slice(0, i),
                    { type: 'op', value: ';' },
                    ...tokens.slice(i, tokens.length),
                ];
            } else if (
                String(tokens[i].value).match(/^\d/) &&
                tokens[i - 1].type !== 'number'
            ) {
                // We need to pop the number off the front of the book name, and
                // insert a semicolon after that number and before the remainder
                // of the book name.
                const t = tokens[i].value.toString();
                const m = t.match(/^(\d+)(.*)$/);
                if (m) {
                    tokens[i].value = m[2]; // new book name, minus the number
                    tokens = [
                        ...tokens.slice(0, i),
                        { type: 'number', value: Number(m[1]) }, // new number token
                        { type: 'op', value: ';' }, // new semicolon token
                        ...tokens.slice(i, tokens.length),
                    ];
                }
            } else {
                // We can just insert a semicolon before the book name. This is
                // the boring case of someone forgetting to add a semicolon.
                tokens = [
                    ...tokens.slice(0, i),
                    { type: 'op', value: ';' }, // new semicolon token
                    ...tokens.slice(i, tokens.length),
                ];
            }
        }
    }
    return tokens;
}
